import { useState } from "react";
import { Link } from "react-router-dom";
import { NOTIFICATION_CATEGORIES, routedConnectionIds, wantsNotification, type NotificationCategory, type SlackNotifyStatusResponse } from "@starsach/shared-types";
import { useSlack } from "../../api/hooks";
import { useAuth } from "../../auth/AuthProvider";
import { Hint } from "../../components/Hint";
import { Card, ErrorState } from "../../components/ui";
import type { TooltipId } from "../../lib/tooltips";

/** Who each category reaches, from the tooltip register; one entry per category, so a new one cannot ship without its line. */
const WHO: Record<NotificationCategory, TooltipId> = {
  "stopped-reporting": "notifications.stopped-reporting",
  "alarms-critical": "notifications.alarms-critical",
  "alarms-other": "notifications.alarms-other",
  "template-available": "notifications.template-available",
  quotas: "notifications.quotas",
  "support-cases": "notifications.support-cases",
  "flow-check": "notifications.flow-check",
  promotions: "notifications.promotions",
  "emergency-close": "notifications.emergency-close",
  "config-not-pulled": "notifications.config-not-pulled",
};

/** #568: where one kind of notification goes in Slack, environment by environment - read only. */
function SlackRoutesFor({ category, status }: { category: NotificationCategory; status: SlackNotifyStatusResponse }) {
  const lines = status.environments.flatMap((e) => {
    const channels = routedConnectionIds(e.routes, category).map((id) => status.connections.find((c) => c.connectionId === id)?.channel ?? "a channel");
    return channels.length ? [`${channels.join(", ")} for ${e.name}`] : [];
  });
  if (lines.length === 0) return <span className="text-neutral-500">Off</span>;
  return (
    <ul className="space-y-0.5">
      {lines.map((l) => (
        <li key={l}>{l}</li>
      ))}
    </ul>
  );
}

/**
 * #564: what this person hears about by email. The ticks come from the same
 * defaults table the sender reads (`wantsNotification`), so a box shown
 * ticked is a box that sends. Saved as part of the preferences: audited,
 * and undoable from Security's changes list.
 */
export function NotificationsSection() {
  const { me, preferences, updatePreferences } = useAuth();
  const [saving, setSaving] = useState<NotificationCategory>();
  const [saved, setSaved] = useState<NotificationCategory>();
  const [error, setError] = useState<unknown>();
  const staff = me?.role === "internal-team" && !me.viewAs;
  // #568: Slack is the organization's, routed per environment - shown here, set under Access › Slack notifications.
  const slack = useSlack(!staff);
  const slackShown = !staff && slack.isSuccess && slack.data.environments.length > 0;
  const routable = slackShown ? slack.data.environments.find((e) => e.canRoute) : undefined;

  const toggle = async (category: NotificationCategory, email: boolean) => {
    setSaving(category);
    setSaved(undefined);
    setError(undefined);
    try {
      await updatePreferences({ notifications: { ...preferences.notifications, [category]: { email } } });
      setSaved(category);
    } catch (e) {
      setError(e);
    } finally {
      setSaving(undefined);
    }
  };

  return (
    <div id="notifications" className="scroll-mt-6">
      <Card title="Notifications">
        <p className="mb-3 text-sm text-neutral-600 dark:text-neutral-400">
          Email about the environments you can see. {staff ? "As Gairm staff you start with everything off - the alarms Slack channel is yours - and anything you turn on covers every organization you may open." : "Alarms open a case either way; this is who hears about it by email."}
        </p>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-neutral-200 text-left dark:border-neutral-800">
              <th scope="col" className="py-2 pr-4 font-medium">Notification</th>
              <th scope="col" className="py-2 font-medium">Email</th>
              {slackShown && <th scope="col" className="py-2 pl-4 font-medium">Slack</th>}
            </tr>
          </thead>
          <tbody>
            {NOTIFICATION_CATEGORIES.map((c) => {
              const on = wantsNotification(preferences.notifications, c.id, "email", staff);
              const id = `notify-${c.id}-email`;
              return (
                <tr key={c.id} className="border-b border-neutral-100 align-top dark:border-neutral-900">
                  <td className="py-2 pr-4">
                    <label htmlFor={id} className="font-medium">{c.label}</label> <Hint id={WHO[c.id]} label={c.label} />
                    <p className="text-xs text-neutral-500">{c.covers} {c.defaults[staff ? "staff" : "customer"].email ? "On unless you turn it off." : "Off unless you turn it on."}</p>
                  </td>
                  <td className="py-2">
                    <input id={id} type="checkbox" className="h-4 w-4" checked={on} disabled={saving !== undefined || Boolean(me?.viewAs)} onChange={(e) => void toggle(c.id, e.target.checked)} />
                    {saving === c.id && <span className="ml-2 text-xs text-neutral-500">Saving…</span>}
                    {saved === c.id && <span role="status" className="ml-2 text-xs text-green-700 dark:text-green-300">Saved</span>}
                  </td>
                  {slackShown && <td className="py-2 pl-4 text-xs"><SlackRoutesFor category={c.id} status={slack.data} /></td>}
                </tr>
              );
            })}
          </tbody>
        </table>
        <p className="mt-3 text-xs text-neutral-500">Sign-in codes, invitations and changes to your own account are always sent, whatever you choose here.</p>
        {slackShown && (
          <p className="mt-2 text-xs text-neutral-500">
            Slack is not a personal choice: your organization sends each environment&apos;s notifications to its Slack channels, as well as email and never instead of it.{" "}
            {routable ? (
              <Link to={`/access/${encodeURIComponent(routable.environmentId)}/slack`} className="underline">
                Change it under Access › Slack notifications
              </Link>
            ) : (
              "Someone who holds connect and route changes it under Access › Slack notifications."
            )}
          </p>
        )}
        {error !== undefined && <ErrorState error={error} />}
      </Card>
    </div>
  );
}
